import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Settings, Wifi, Music, ShieldCheck, Share2, Lock, Users, Fingerprint, Wand2 } from 'lucide-react';
import type { Sip } from '@sbclite/core';
import { useCreateSipPeer, useUpdateSipPeer } from '../../../hooks/useSipPeers';
import { useSipProfiles } from '../../../hooks/useSipProfiles';
import { useSipInterfaces } from '../../../hooks/useSipInterfaces';
import { createFormErrorHandler } from '../../../utils/form-error-handler';
import { ResourceEditor, firstTabWithError, type ResourceEditorTab } from '../../common';
import {
  sipPeerCreateSchema,
  buildDefaultValues,
  parseSourceIpRangesToArray,
  type SipPeerFormData,
  type SipPeer,
  type TemplateKey,
} from './schema';
import { GeneralTab } from './tabs/GeneralTab';
import { InterfaceTab } from './tabs/InterfaceTab';
import { MediaTab } from './tabs/MediaTab';
import { AuthTab } from './tabs/AuthTab';
import { RoutingTab } from './tabs/RoutingTab';
import { TlsTab } from './tabs/TlsTab';
import { GroupsTab } from './tabs/GroupsTab';
import { IdentityTab } from './tabs/IdentityTab';
import { ManipulationsTab } from './tabs/ManipulationsTab';
import { ProfileTab } from './tabs/ProfileTab';
import { EffectivePolicyTab } from './tabs/EffectivePolicyTab';

function stripProfileManagedPeerFields(data: Sip.PeerCreateInput): Sip.PeerCreateInput {
  const metadata = data.metadata ? { ...data.metadata } : undefined;
  if (metadata) {
    delete metadata.outboundMinSessionExpiresSeconds;
    delete metadata.minSessionExpiresSeconds;
  }

  const mediaConfig = data.mediaConfig ? { ...data.mediaConfig } : undefined;
  if (mediaConfig) {
    delete mediaConfig.srtp;
    delete mediaConfig.sdpStripIce;
    delete mediaConfig.sdpOutboundProfile;
    delete mediaConfig.cryptoSuites;
    delete mediaConfig.expandCryptoSuites;
  }

  return {
    ...data,
    metadata,
    mediaConfig,
    registerExpiresDefaultSeconds: undefined,
    registerExpiresMinSeconds: undefined,
    registerExpiresMaxSeconds: undefined,
    registerMaxContacts: undefined,
    registerAllowWildcardContact: undefined,
    registerRequireContact: undefined,
  };
}

interface SipPeerEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onSaved?: (peer: Sip.PeerInfo) => void;
  initialValues?: Partial<SipPeerFormData>;
  /** When set, the editor is in edit mode. */
  editingPeer?: SipPeer | null;
  /** Create-time template seed (ignored when editingPeer is set). */
  templateKey?: TemplateKey | null;
}

export function SipPeerEditor({ isOpen, onClose, onSuccess, onSaved, initialValues, editingPeer, templateKey }: SipPeerEditorProps) {
  const [activeTab, setActiveTab] = useState('general');

  const formErrorHandler = createFormErrorHandler({
    componentName: 'SipPeerEditor',
    showFieldErrors: true,
  });

  const form = useForm<SipPeerFormData, undefined, SipPeerFormData>({
    resolver: zodResolver(sipPeerCreateSchema),
    defaultValues: buildDefaultValues({ editingPeer, templateKey }),
  });

  // Reset form + active tab whenever the editor opens or its target changes.
  useEffect(() => {
    if (!isOpen) return;
    form.reset({ ...buildDefaultValues({ editingPeer, templateKey }), ...(!editingPeer ? initialValues : {}) });
    setActiveTab('general');
  }, [isOpen, editingPeer, templateKey, initialValues, form]);

  const createPeerMutation = useCreateSipPeer();
  const updatePeerMutation = useUpdateSipPeer();

  // Derive transport from the selected interface to gate the TLS tab.
  const { data: sipInterfacesData } = useSipInterfaces();
  const { data: sipProfilesData } = useSipProfiles();
  const sipInterfaceId = form.watch('sipInterfaceId');
  const sipProfileId = form.watch('sipProfileId');
  const attachedProfileId = typeof sipProfileId === 'string' && sipProfileId.length > 0 ? sipProfileId : null;
  const routingConfig = form.watch('routingConfig');
  const outboundTlsConfig = form.watch('outboundTlsConfig');
  const selectedProfile = sipProfilesData?.data.find((profile) => profile.id === attachedProfileId) ?? null;
  const legacyPolicyLocked = attachedProfileId !== null;
  const transport = sipInterfacesData?.find((i) => i.id === sipInterfaceId)?.protocol as
    | 'UDP'
    | 'TCP'
    | 'TLS'
    | undefined;

  // Ensure nested config objects exist for field registration (endpoints array, TLS block).
  // Written as a single whole-object setValue so the paths stay fully typed
  // (deep paths under a nullable routingConfig are not resolvable by RHF's Path type).
  useEffect(() => {
    if (!isOpen) return;
    const rc = routingConfig ?? {};
    if (!routingConfig || !rc.endpointSelectionMethod || !Array.isArray(rc.endpoints)) {
      form.setValue('routingConfig', {
        ...rc,
        endpointSelectionMethod: rc.endpointSelectionMethod ?? 'round-robin',
        endpoints: Array.isArray(rc.endpoints) ? rc.endpoints : [],
      });
    }
    if (transport === 'TLS' && !outboundTlsConfig) {
      form.setValue('outboundTlsConfig', {});
    }
    // Clear stale TLS config when the selected interface resolves to a non-TLS
    // transport (mirrors SipInterfaceEditor). Only when the interface is actually
    // resolved — `transport` is undefined while interfaces are loading.
    if (transport !== undefined && transport !== 'TLS' && outboundTlsConfig) {
      form.setValue('outboundTlsConfig', null);
    }
  }, [isOpen, routingConfig, outboundTlsConfig, transport, form]);

  const handleMutationSuccess = (response: { success: boolean; data: Sip.PeerInfo }) => {
    if (!response.success) { formErrorHandler.onError(new Error('Peer was not saved')); return; }
    onSaved?.(response.data);
    onSuccess?.();
    onClose();
    form.reset(buildDefaultValues({ editingPeer: null, templateKey: null }));
    setActiveTab('general');
  };

  const onSubmit = (data: SipPeerFormData) => {
    // Schema + peerCreateRefinements already validated sipInterfaceId, sourceIpRanges
    // (carrier/teams), identification, OPTIONS ping, and domain (min 1 char).
    // Below are transforms only — no hidden defaults.
    const { sourceIpRanges, ...rest } = data;
    const payload: Sip.PeerCreateInput = {
      ...rest,
      sipUri: rest.sipUri || `sip:peer@${rest.domain}`,
      sourceIpRanges: parseSourceIpRangesToArray(sourceIpRanges),
    };
    const submitPayload = legacyPolicyLocked ? stripProfileManagedPeerFields(payload) : payload;

    if (editingPeer) {
      updatePeerMutation.mutate(
        { peerId: editingPeer.id, data: submitPayload },
        { onSuccess: handleMutationSuccess, onError: formErrorHandler.onError }
      );
    } else {
      createPeerMutation.mutate(submitPayload, {
        onSuccess: handleMutationSuccess,
        onError: formErrorHandler.onError,
      });
    }
  };

  const tabs: ResourceEditorTab[] = useMemo(
    () => [
      {
        id: 'general',
        label: 'General',
        icon: Settings,
        fields: ['name', 'type', 'domain', 'enabled', 'metadata.description'],
        render: () => <GeneralTab form={form} />,
      },
      {
        id: 'profile',
        label: 'Profile',
        icon: ShieldCheck,
        fields: ['sipProfileId'],
        render: () => <ProfileTab form={form} profiles={sipProfilesData?.data ?? []} profileLocked={legacyPolicyLocked} />,
      },
      {
        id: 'interface',
        label: 'Interface',
        icon: Wifi,
        fields: ['sipInterfaceId', 'sourceIpRanges'],
        render: () => <InterfaceTab form={form} />,
      },
      {
        id: 'media',
        label: 'Media',
        icon: Music,
        fields: ['mediaRealmId', 'mediaConfig'],
        render: () => <MediaTab form={form} />,
      },
      {
        id: 'auth',
        label: 'Auth',
        icon: ShieldCheck,
        fields: [
          'authRequired',
          'registrationRequired',
          'registrationPolicy',
          'authConfig',
          'username',
          'realm',
          'registrationUri',
        ],
        render: () => <AuthTab form={form} profileLocked={legacyPolicyLocked} profileName={selectedProfile?.name ?? null} />,
      },
      {
        id: 'routing',
        label: 'Routing',
        icon: Share2,
        fields: ['routingConfig', 'sipUri'],
        render: () => <RoutingTab form={form} />,
      },
      {
        id: 'tls',
        label: 'TLS',
        icon: Lock,
        fields: ['outboundTlsConfig'],
        hidden: transport !== 'TLS',
        render: () => <TlsTab form={form} />,
      },
      {
        id: 'groups',
        label: 'Groups',
        icon: Users,
        fields: ['groupId', 'groupWeight', 'groupPriority', 'groupEnabled', 'codecGroupId'],
        render: () => <GroupsTab form={form} />,
      },
      {
        id: 'identity',
        label: 'Identity',
        icon: Fingerprint,
        fields: [
          'hostId',
          'metadata.userAgent',
          'metadata.teamsConfig',
          'metadata.outboundMinSessionExpiresSeconds',
          'metadata.minSessionExpiresSeconds',
        ],
        render: () => <IdentityTab form={form} profileLocked={legacyPolicyLocked} profileName={selectedProfile?.name ?? null} />,
      },
      {
        id: 'effective-policy',
        label: 'Effective policy',
        icon: Lock,
        fields: [],
        hidden: !editingPeer,
        render: () => <EffectivePolicyTab peerId={editingPeer?.id} />,
      },
      {
        id: 'manipulations',
        label: 'Manipulations',
        icon: Wand2,
        fields: ['inboundManipulationId', 'outboundManipulationId'],
        render: () => <ManipulationsTab form={form} />,
      },
    ],
    [editingPeer, form, legacyPolicyLocked, selectedProfile?.name, sipProfilesData?.data, transport]
  );

  const onInvalid = (errors: typeof form.formState.errors) => {
    const target = firstTabWithError(errors, tabs);
    if (target) setActiveTab(target);
    formErrorHandler.onInvalid(errors);
  };

  const isSaving = createPeerMutation.isPending || updatePeerMutation.isPending;

  return (
    <ResourceEditor
      isOpen={isOpen}
      onClose={onClose}
      title={editingPeer ? 'Edit SIP Peer' : 'Add SIP Peer'}
      subtitle={
        editingPeer
          ? 'Update SIP peer configuration with RFC 3261 compliance'
          : 'Configure a new RFC 3261 compliant SIP peer'
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      errors={form.formState.errors}
      onSubmit={form.handleSubmit(onSubmit, onInvalid)}
      onCancel={onClose}
      isSaving={isSaving}
      saveLabel={editingPeer ? 'Update Peer' : 'Create Peer'}
      savingLabel={editingPeer ? 'Updating...' : 'Creating...'}
    />
  );
}

export default SipPeerEditor;
